/**
 * backfillGuard — which SharePoint lists a workbook-sourced import may ever
 * write, and which it may never touch. The rule, made into code.
 *
 * WHY A MODULE AND NOT A COMMENT. Mikko, 2026-09-06: "Production Grid, BQC3
 * should not be touch, also the iSyn_WIPs." The reason is structural and easy
 * to lose track of: some lists are read by more than one screen, so a
 * "backfill" that wrote workbook history into them would move figures on
 * screens nobody was looking at. iSyn2_CutReadyScans is the sharpest case —
 * BQC2 writes it, BQC3 reads it AND writes it (a dispatch with no cut scan
 * creates one), and the Production Grid reads it to build the Dispatched KPI.
 * The rule against writing history into it has to survive the next feature
 * that wants to "just sync one more thing", and a comment does not.
 *
 * SCOPE. This guards one class of write: a BULK write of records the app did
 * not itself witness — rows copied from a workbook sheet, a CSV, a one-off
 * migration. It does not guard live station writes: BQC2 recording a scan an
 * operator just took is the app witnessing something, and belongs in
 * iSyn2_CutReadyScans exactly as it always has. A backfill service calls
 * assertBackfillTarget() before its first write; a station never calls it.
 *
 * THE TWO SETS ARE DERIVED FROM THE WIRING, not from taste — see the
 * reads/writes map on the "i-SYNERGY Loop" page and useProductionWips.ts:337
 * onward for what the Grid actually loads. If that wiring changes, this
 * changes with it, and the test pins both lists so the change is deliberate.
 */

/** Lists a workbook-sourced import is permitted to write into. Each is read
 *  and written by ONE station screen and by nothing the Production Grid or
 *  BQC3 loads. */
export const BACKFILL_ALLOWED: readonly string[] = [
  "iSyn2_ReceivingScans",   // BQC4 writes; BQC5 reads only for repair returns
  "iSyn2_QcScans",          // BQC5 writes; BQC4 reads only for repair returns
  // SUBCONTRACTORS · Payroll's own ledger. Read by Records, Bank File and the
  // Run's "already paid" cursor — all in src/modules/subcontractors — and by
  // nothing else. Added 2026-09-11 for the RECORDS OF PAYOUT / NON-BIR
  // RECORDS import (payoutLedgerImport.ts), which Mikko asked for.
  "iSyn2_SubconPayouts",
  // Manu Monitoring's own order-status table (OrderStatusService). Read by
  // that one screen and by nothing else — not the Production Grid, not a
  // station, not payroll. Added 2026-09-16 for the CANCELLED_COMPLETED ORDERS
  // import Mikko asked for, and it exists BECAUSE of this rule rather than in
  // spite of it: the statuses belong on iSyn_WIPs by every other measure, and
  // iSyn_WIPs is the one list a bulk import may never touch. So they live
  // beside the line and are joined at read time on ORDER ID.
  "iSyn2_OrderStatus",
  // BQC1's stored picture of the FOR CUT sheet (ForCutSheetService). It is
  // rows copied from a workbook sheet, which is exactly what this rule is
  // about — and it is allowed for exactly the reason the rule allows anything:
  // Prod Tag and Cutter Assigning are the only screens that read it, and
  // nothing the Production Grid or BQC3 loads touches it. It is also not a
  // ledger. Replaced WHOLE on every upload, so it can hold no history to
  // corrupt: the sheet stays the planner's in Excel, and this is a copy of it.
  // Listed here 2026-09-19 so the write is deliberate rather than unexamined —
  // it had been happening from Prod Tag without ever passing this gate.
  "iSyn2_ForCutSheet",
  // IG COM's stored copy of the Shopify order ledger (IgcomOrdersService).
  // Rows the app did not witness — they happened in a webstore — so this is a
  // backfill by definition, and it is allowed for the usual reason: the IG COM
  // screen under CRM is the only reader, and nothing the Production Grid, a
  // station or payroll loads touches it. Added 2026-09-19 on Mikko's decision
  // ("i want to store data so i have analysis later"), to make repeat-buyer
  // and trend analysis possible without re-reading Shopify on every load.
  // PSEUDONYMOUS BY CONSTRUCTION: the list holds a customer KEY and order
  // facts, never an email, a phone or a street address — see the service.
  // It is on the CRM SITE, not Manufacturing, hence the plain name: this
  // guard is about who READS a list, which is a question the site does not
  // answer either way.
  "IGCOM_Orders",
  // CRM · TikTok Shop's stored copy of the marketplace order ledger
  // (TiktokOrdersService), beside IGCOM_Orders on the CRM site. Added
  // 2026-09-20, and allowed on the same test as its neighbour: the TikTok Shop
  // screen under CRM is the only reader, and nothing the Production Grid, a
  // station or payroll touches it.
  //
  // NARROWER THAN IGCOM_Orders, not merely equal to it. That list stores a
  // buyer NAME, safely, because Shopify's names reach every IG COM viewer
  // unmasked. TikTok's do not — they are masked server-side for anyone off
  // IGCOM_PII_USERS — so a stored name would be whatever the person who
  // pressed Sync happened to have the right to see. This list therefore holds
  // NO buyer name and no contact detail of any kind: an order's facts, its
  // city and province, its SKUs, and a buyer key only where TikTok supplied a
  // real one. See tiktokOrdersStore.ts.
  "TIKTOK_Orders",
  // CRM · Lazada Shop's stored copy of the marketplace order ledger
  // (LazadaOrdersService), beside IGCOM_Orders and TIKTOK_Orders on the CRM
  // site. Added 2026-09-21, on the same test as both: the Lazada Shop screen
  // under CRM is the only reader, and nothing the Production Grid, a station
  // or payroll touches it.
  //
  // NARROWER AGAIN. TIKTOK_Orders keeps a buyer key where TikTok supplies a
  // real one; this list keeps NO buyer identity at all, because Lazada offers
  // none — the order carries no customer id, and the app's Sensitive Data
  // Privilege is Mask, so names, phones and streets are redacted before they
  // reach us. There is not even an empty column for one: an always-empty
  // field invites somebody to fill it later from whatever looks
  // person-shaped, which on this API is a redacted name. See
  // lazadaOrdersStore.ts.
  "LAZADA_Orders",
];

/** Lists a workbook-sourced import must never write, and why. */
export const BACKFILL_FORBIDDEN: Readonly<Record<string, string>> = {
  iSyn_WIPs:
    "The Production Grid's own table, rewritten wholesale by its import; every station reads it.",
  iSyn2_CutReadyScans:
    "Shared three ways — BQC2 writes, BQC3 reads and writes, the Grid reads it for the Dispatched KPI.",
  iSyn2_DispatchSlips:
    "BQC3's record of what went out; the Grid and BQC4/BQC5 read it.",
  iSyn2_Assignments:
    "BQC3's assignment events.",
  iSyn2_InHouseAssignments:
    "Read by both BQC3 and the Production Grid.",
  iSyn2_DryRunLog:
    "BQC3's dry-run picks.",
  iSyn2_CutterAssignments:
    "BQC1's record of decisions PEOPLE made about who cuts what — the app witnessed every one of them, "
    + "and a bulk write would put decisions nobody took into the evidence a future Smart Fill learns from.",
  iSyn_Batches:
    "Production Grid.",
  iSyn_StageHistory:
    "Production Grid.",
  iSyn_GridDailyOutput:
    "Production Grid.",
};

export class BackfillForbiddenError extends Error {
  public readonly list: string;
  constructor(list: string, why: string) {
    super(`Refusing to backfill ${list}: ${why}`);
    this.name = "BackfillForbiddenError";
    this.list = list;
  }
}

/** True only for a list on the allow-list. Anything unknown is NOT allowed —
 *  a new list has to be added here on purpose, with its wiring checked. */
export function isBackfillTarget(list: string): boolean {
  return BACKFILL_ALLOWED.includes(list);
}

/**
 * Throws unless `list` may take a bulk import of workbook-sourced rows.
 *
 * A forbidden list throws with its reason; an unknown list throws too, because
 * "not on either list" means nobody has checked who reads it yet.
 */
export function assertBackfillTarget(list: string): void {
  if (isBackfillTarget(list)) return;
  const why = BACKFILL_FORBIDDEN[list] ?? "not on the allow-list — nobody has checked who else reads this list.";
  throw new BackfillForbiddenError(list, why);
}
